'use client';
import {
    faBook,
    faBorderAll,
    faChalkboardTeacher,
    faChevronRight,
    faHorse,
    faInbox,
    faUser,
    faUsers
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion, Variants } from 'framer-motion';
import Link from 'next/link';
import { Badge } from '~/components/ui';
import CountUp from '~/components/ui/AnimatedComponents/CountUp';
import { modules as moduleData } from '~/data/modules/modules';
import SplitText from '../../AnimatedComponents/SplitText';

const totalFunctions = moduleData.reduce((count, module) => count + (module.functions?.length ?? 0), 0);

export const moduleIconMap: any = {
    platzbuchungen: faBorderAll,
    'online-anmeldungen': faUser,
    mitgliederverwaltung: faUsers,
    trainingsjournal: faBook,
    journal: faBook,
    trainingsplanerstellung: faChalkboardTeacher,
    trainerstunden: faChalkboardTeacher,
    reiten: faHorse,
    vereinskommunikation: faInbox
};

const heroStats = [
    { label: 'Module im Stack', value: moduleData.length },
    { label: 'Funktionen bereit', value: totalFunctions }
];

const heroShowcase = moduleData.slice(0, 3).map((module) => ({
    title: module.title,
    slug: module.slug,
    highlight: module.functions?.[0]?.descriptionShort ?? module.description,
    icon: moduleIconMap[module.slug] ?? faChevronRight,
    variant: 'Digital'
}));

const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.15,
            delayChildren: 0.2
        }
    }
};

const itemVariants: Variants = {
    hidden: { opacity: 0, x: 20 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { type: 'spring', stiffness: 50 }
    }
};

const fadeIn: Variants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.6, ease: 'easeOut' }
    }
};

const ModuleHero = () => {
    return (
        <section className="tw-rounded-3xl tw-border tw-border-solid tw-border-accents-2 tw-bg-gradient-to-r tw-from-success/5 tw-to-success/15 tw-px-2 tw-py-5 md:tw-px-10 lg:tw-py-20">
            <div className="tw-z-10 tw-grid tw-min-w-0 md:tw-gap-12 lg:tw-grid-cols-12 lg:tw-items-center">
                {/* Left Column: Text & Stats */}
                <motion.div
                    className="tw-min-w-0 md:tw-space-y-8 lg:tw-col-span-7"
                    initial="hidden"
                    animate="visible"
                    variants={fadeIn}
                >
                    <div className="tw-space-y-6">
                        <Badge flat>Module orchestriert</Badge>

                        <h1 className="tw-text-4xl tw-font-bold tw-leading-[1.1] tw-text-accents-9 md:tw-text-5xl lg:tw-text-6xl">
                            <SplitText
                                className="tw-flex-wrap"
                                splitBy="word"
                                duration={0.5}
                                text={'Ein modularer Stack, der zusammen schneller wächst.'}
                            />
                        </h1>

                        <p className="tw-text-lg tw-leading-relaxed tw-text-accents-7 tw-text-center md:tw-text-left md:tw-text-xl">
                            Baue dein System aus flexiblen Bausteinen. Jede Funktion ist sofort einsatzbereit und lässt
                            sich in wenigen Klicks mit den anderen Sportision Modulen kombinieren.
                        </p>
                    </div>

                    {/* Stats Section */}
                    <div className="tw-flex tw-flex-wrap tw-justify-around tw-gap-8 tw-border-t tw-border-accents-2 tw-pt-8">
                        {heroStats.map((stat) => (
                            <div key={stat.label} className="tw-flex tw-items-center md:tw-items-start tw-flex-col">
                                <div className="tw-flex tw-items-center tw-gap-1">
                                    <CountUp
                                        to={stat.value}
                                        className="tw-text-4xl tw-font-extrabold tw-tracking-tight tw-text-accents-9"
                                    />
                                    <span className="tw-text-3xl tw-font-bold tw-text-success">+</span>
                                </div>
                                <p className="tw-text-sm tw-font-medium tw-uppercase tw-tracking-wide tw-text-accents-6">
                                    {stat.label}
                                </p>
                            </div>
                        ))}
                    </div>
                </motion.div>

                {/* Right Column: Showcase Card */}
                <div className="tw-relative tw-min-w-0 lg:tw-col-span-5 tw-mt-6 md:tw-mt-0">
                    <motion.div
                        className="tw-card tw-relative tw-z-10 tw-w-full tw-overflow-hidden tw-rounded-3xl tw-border lg:tw-shadow-2xl"
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.2 }}
                    >
                        <div className="tw-border-b tw-border-accents-2 tw-bg-white/50 tw-p-2">
                            <div className="tw-flex tw-items-center tw-justify-between">
                                <div>
                                    <p className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-text-accents-7">
                                        System Übersicht
                                    </p>
                                    <p className="tw-text-lg tw-font-bold tw-text-success">Die beliebtesten Module </p>
                                </div>
                            </div>
                        </div>

                        {/* Staggered List Container */}
                        <motion.div
                            className="tw-space-y-3"
                            variants={containerVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            {heroShowcase.map((module, i) => (
                                <motion.div
                                    key={module.title + i}
                                    variants={itemVariants}
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    <Link
                                        href={module.slug}
                                        className="tw-card tw-flex tw-cursor-pointer tw-items-start tw-gap-4 tw-rounded-xl tw-p-4 tw-transition-colors tw-duration-300 hover:tw-bg-successLight/60"
                                    >
                                        <div className="tw-flex tw-h-12 tw-w-12 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-bg-gradient-to-br tw-from-success/10 tw-to-success/5 tw-text-success">
                                            <FontAwesomeIcon icon={module.icon} className="tw-text-xl" />
                                        </div>
                                        <div className="tw-min-w-0 tw-flex-1 tw-space-y-1">
                                            <div className="tw-flex tw-items-center tw-justify-between">
                                                <span className="tw-text-sm tw-font-bold tw-text-accents-9">
                                                    {module.title}
                                                </span>
                                                <Badge flat size="xs">
                                                    {module.variant}
                                                </Badge>
                                            </div>
                                            <p className="tw-truncate tw-text-sm tw-text-accents-6">
                                                {module.highlight}
                                            </p>
                                        </div>
                                    </Link>
                                </motion.div>
                            ))}
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default ModuleHero;
